import * as JobDetailActions from '../job-detail/+state/job-detail.actions';
import * as JobDetailSelectors from '../job-detail/+state/job-detail.selectors';
import * as _ from 'lodash';
import * as moment from 'moment';

import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	OnDestroy,
} from '@angular/core';
import { FieldTeam, Job } from 'src/shared/api';
import { Observable, Subscription, merge, combineLatest } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
	UpdateOnlyAvailableRwics,
	UpdateSearchText,
	UpdateSelectedTeamId,
} from './+state/field-worker/field-worker.actions';
import { compareTeam, displayFieldTeam } from 'src/shared/enums/enums';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
	selectAnyJobShiftChanges,
	selectJobShiftLoading,
} from '../+state/job-shift/job-shift.selectors';

import { AppState } from 'src/admin-app/shared/+state';
import { CalendarParams } from 'src/shared/models/ListArgsParams';
import { FormBuilder } from '@angular/forms';
import { JobScheduleDialogContext } from '../shared/job-schedule-dialog/job-schedule-dialog.component';
import { LookUpsService } from 'src/shared/lookups.service';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { dateParamFormat } from 'src/shared/constants';
import { jobsFragments } from '../jobs.routes';
import { parseDateParam } from 'src/shared/utilities/utility-functions';
import { selectFieldWorkersLoading } from './+state/field-worker/field-worker.selectors';

@Component({
	templateUrl: './job-assignments.component.html',
	styleUrls: ['./job-assignments.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobAssignmentsComponent
	extends SelfDestruct
	implements OnInit, OnDestroy {
	readonly jobsFragments = jobsFragments;
	routeAndQueryParams = combineLatest([
		this.route.params,
		this.route.queryParamMap,
	]).pipe(
		map(([routeParams, queryParams]) => {
			return {
				...this.paramMapAsCalendarStartDate(queryParams),
				id: routeParams.id,
			};
		})
	);

	displayFieldTeam = displayFieldTeam;
	readonly compareTeam = compareTeam;
	jobScheduleDialogContext = JobScheduleDialogContext;
	fieldTeams$: Observable<FieldTeam[]>;

	shiftChanges$: Observable<{}>;

	job$ = this.store.pipe(
		select(JobDetailSelectors.getSelectedJob),
		filter<Job>(Boolean)
	);

	routeSubsc: Subscription;

	loading$ = merge(
		this.store.pipe(select(selectJobShiftLoading)),
		this.store.pipe(
			select(JobDetailSelectors.getSelectedJob),
			map((job) => !job)
		)
	);

	loadingRwics$ = this.store.pipe(select(selectFieldWorkersLoading));

	private allTeams = {
		id: 0,
		name: 'All Teams',
		railroad: {
			name: 'All Teams',
		},
	} as FieldTeam;

	calendarFilters = this.fb.group({
		byTeam: this.fb.control<FieldTeam[]>([]),
		byRWICAvailablity: [false],
		byRWICName: [''],
		byStartDate: [],
	});

	startDate = moment.utc(this.calendarFilters.get('byStartDate').value);

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private route: ActivatedRoute,
		private router: Router,
		private readonly lookupsService: LookUpsService
	) {
		super();
	}

	ngOnInit() {
		this.listenToCalendarFilters();
		this.fieldTeams$ = this.getTeams();

	this.job$
 	   .pipe(
  	    	takeUntil(this.destroyed),
        	map(job => job.id),
        	distinctUntilChanged(),
        	switchMap(() => combineLatest([this.job$, this.fieldTeams$]).pipe(take(1)))
    	)
    	.subscribe(([job, teams]) => {
        	const defaultTeam = teams.find(t => t.id === job.team.id);
        	const defaultTeamArr = defaultTeam ? [defaultTeam] : [];

        	this.calendarFilters.patchValue(
            	{ byTeam: defaultTeamArr },
            	{ emitEvent: false }
        	);

        	const selectedTeamIds = defaultTeamArr.map((t: FieldTeam) => t.id);
        	this.store.dispatch(new UpdateSelectedTeamId({
        	    selectedTeamId: selectedTeamIds.length ? selectedTeamIds : [-1]
        	}));
    	});

		this.shiftChanges$ = this.routeAndQueryParams.pipe(
			takeUntil(this.destroyed),
			map((p) => p.id),
			filter<number | string>(Boolean),
			switchMap((jobId) =>
				this.store.pipe(select(selectAnyJobShiftChanges(jobId)))
			)
		);

		this.routeAndQueryParams
			.pipe(
				takeUntil(this.destroyed),
				tap((p) => {
					this.calendarFilters
						.get('byStartDate')
						.setValue(p.sd, { emitEvent: false });
					this.startDate = moment.utc(
						this.calendarFilters.get('byStartDate').value
					);
				})
			)
			.subscribe();
	}

	addShifts(): void {
		const startDate = moment.utc(this.startDate).startOf('w');
		const endDate = moment.utc(this.startDate).endOf('w');
		this.store.dispatch(
			JobDetailActions.openAddShiftDialog({ startDate, endDate })
		);
	}

	getTeams(): Observable<FieldTeam[]> {
		return this.lookupsService.fieldTeams.pipe(
	 	   map((teams) =>
    	  _([...teams, this.allTeams])
        	.orderBy((ft) => [
          	// Put All Teams at top, then by name
          	ft.id === 0 ? 0 : 2,
          	(ft.name || '').toLowerCase(),
        	])
        	.value()
  	  		)
 	 	);
	}

	listenToCalendarFilters() {
		this.calendarFilters
			.get('byStartDate')
			.valueChanges.pipe(takeUntil(this.destroyed))
			.subscribe((sd: moment.Moment) => this.startDateChange(sd));

		this.calendarFilters
			.get('byRWICName')
			.valueChanges.pipe(takeUntil(this.destroyed))
			.subscribe((searchText) => {
				this.store.dispatch(new UpdateSearchText({ searchText }));
			});

		this.calendarFilters
			.get('byRWICAvailablity')
			.valueChanges.pipe(takeUntil(this.destroyed))
			.subscribe((onlyAvailableRwics) => {
				const onlyAvailable = onlyAvailableRwics;
				this.store.dispatch(
					new UpdateOnlyAvailableRwics({
						onlyAvailableRwics: onlyAvailable,
					})
				);
			});

			this.calendarFilters.get('byTeam').valueChanges.pipe(
				takeUntil(this.destroyed)
			).subscribe((selectedTeams: FieldTeam[]) => {
				const selectedTeamIds = selectedTeams.map(team => team.id);
				if (selectedTeamIds.length === 0) {
					this.store.dispatch(new UpdateSelectedTeamId({ selectedTeamId: [-1]})) // Shows no field workers
				} else if (selectedTeams.some((team) => team.id === this.allTeams.id)) {
					this.store.dispatch(new UpdateSelectedTeamId({ selectedTeamId: [] }));
				} else {
					this.store.dispatch(new UpdateSelectedTeamId({ selectedTeamId: selectedTeamIds }));
				}
			});
	}

	startDateChange(startDate: moment.Moment) {
		this.router.navigate([], {
			queryParams: { sd: moment.utc(startDate).format(dateParamFormat) },
			relativeTo: this.route,
		});
	}

	ngOnDestroy() {
		this.store.dispatch(new UpdateSearchText({ searchText: '' }));
		super.ngOnDestroy();
	}

	private paramMapAsCalendarStartDate(paramMap: ParamMap): CalendarParams {
		return {
			['sd']:
				parseDateParam(paramMap.get('sd')) || moment.utc().startOf('w'),
		};
	}
}
